import React, { useState } from 'react';
import { FormattedAIResponse } from '@/components/FormattedAIResponse';
import { ArrowUpRight } from 'lucide-react';

export interface VideoSummaryResult {
  url: string;
  transcript: string;
  summary: string; // markdown
}

interface Props {
  result: VideoSummaryResult;
  isDarkMode?: boolean;
}

export const VideoSummaryCard: React.FC<Props> = ({ result, isDarkMode = false }) => {
  const [expanded, setExpanded] = useState(false);
  const wrapper = isDarkMode ? 'border-slate-800 text-slate-100' : 'border-slate-200 text-slate-900';
  const headerBg = isDarkMode ? 'bg-violet-500/10 border-violet-400/30' : 'bg-violet-50 border-violet-200';
  const metaText = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className={`border ${wrapper} rounded-2xl overflow-hidden`} data-video-summary-react>
      <div className={`${headerBg} border-b p-4 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.35em] uppercase opacity-70">Video Summary</p>
          </div>
        </div>
      </div>

      <div className="prose prose-sm dark:prose-invert px-4 py-5">
        <FormattedAIResponse content={result.summary} isStreaming={false} isDarkMode={isDarkMode} />
      </div>

      <div className="px-4 pb-4" data-transcript-section>
        <div className="flex items-center justify-between">
          <button
            type="button"
            className={`text-xs ${metaText} cursor-pointer inline-flex items-center gap-1 hover:opacity-80 select-none`}
            onClick={() => setExpanded(v => !v)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
            <span className="toggle-text">{expanded ? 'Hide raw transcript' : 'Show raw transcript'}</span>
          </button>
          <a href={result.url} target="_blank" rel="noopener" className="text-xs decoration-none">Video<ArrowUpRight className="inline h-4 w-4" /></a>
        </div>
        {expanded && (
          <pre className={`mt-2 max-h-64 overflow-auto text-[11px] ${metaText} whitespace-pre-wrap`}>{result.transcript}</pre>
        )}
      </div>
    </div>
  );
};
